import type { ColDef, ColGroupDef } from 'ag-grid-enterprise';

import { dateTimeRenderer } from '@/utils/GridRenderer.utils';

import {
  renderInfoCell,
  renderResponseActionTriggerCell,
  renderStatusCell
} from './TicketResponseActions.renderer';
import type {
  ResponseActionStatus,
  ResponseActionStatusReason
} from './TicketResponseActions.types';

export enum RESPONSE_ACTION_STATUS {
  Completed = 'completed',
  Error = 'error',
  Skipped = 'skipped'
}

export const RESPONSE_ACTION_STATUS_COLORS: Record<
  ResponseActionStatus,
  string
> = {
  [RESPONSE_ACTION_STATUS.Completed]: 'success',
  [RESPONSE_ACTION_STATUS.Error]: 'error',
  [RESPONSE_ACTION_STATUS.Skipped]: 'info'
};

export const RESPONSE_ACTION_STATUS_LABELS: Record<
  Exclude<ResponseActionStatus, 'skipped'>,
  string
> = {
  completed: 'Completed',
  error: 'Error'
};

export const RESPONSE_ACTION_STATUS_REASON_LABELS: Record<
  ResponseActionStatusReason,
  string
> = {
  duplicate: 'Duplicate',
  excluded_target: 'Excluded Target',
  incomplete_action: 'Incomplete Action',
  manual_only: 'Manual Only',
  missing_product: 'Missing Product',
  not_configured: 'Not Configured',
  not_enabled: 'Not Enabled',
  not_implemented: 'Not Implemented',
  restricted_environment: 'Restricted Environment',
  target_not_found: 'Target Not Found'
};

export const VISIBLE_RESPONSE_ACTIONS: ResponseActionStatus[] = [
  RESPONSE_ACTION_STATUS.Error
];

export const RESPONSE_ACTION_TYPES = {
  crowdstrike_isolate_host: 'CrowdStrike - Isolate Host',
  crowdstrike_release_host_from_isolation:
    'CrowdStrike - Release Host from Isolation',
  escalate_to_ticket: 'Escalate to Ticket',
  google_workspace_isolate_user: 'Google Workspace - Isolate User',
  google_workspace_reset_password: 'Google Workspace - Reset Password',
  google_workspace_revoke_session: 'Google Workspace - Revoke Session',
  isolate_endpoint: 'Isolate Endpoint',
  microsoft_isolate_host: 'Microsoft - Isolate Host',
  microsoft_release_host_from_isolation:
    'Microsoft - Release Host from Isolation',
  microsoft_reset_password: 'Microsoft - Reset Password',
  microsoft_revoke_session: 'Microsoft - Revoke Session',
  sentinelone_isolate_host: 'SentinelOne - Isolate Host',
  sentinelone_release_host_from_isolation:
    'SentinelOne - Release Host from Isolation',
  submit_feedback: 'Submit Feedback',
  unisolate_endpoint: 'Unisolate Endpoint',
  user_escalate_to_ticket: 'User - Escalate to Ticket'
};

export const RESPONSE_ACTION_TARGETS = {
  crowdstrike_isolate_host: 'endpoint_id',
  crowdstrike_release_host_from_isolation: 'endpoint_id',
  google_workspace_reset_password: 'user_key',
  google_workspace_revoke_session: 'user_key',
  microsoft_isolate_host: 'endpoint_id',
  microsoft_release_host_from_isolation: 'endpoint_id',
  microsoft_reset_password: 'target_upn',
  microsoft_revoke_session: 'target_upn',
  sentinelone_isolate_host: 'endpoint_id',
  sentinelone_release_host_from_isolation: 'endpoint_id'
};

export const RESPONSE_ACTION_COLUMNS: (ColDef | ColGroupDef)[] = [
  {
    field: 'current_status',
    headerName: 'Status',
    width: 170,
    minWidth: 170,
    suppressHeaderMenuButton: true,
    resizable: false,
    cellRenderer: renderStatusCell
  },
  {
    field: 'response_action_trigger',
    headerName: '',
    width: 80,
    suppressHeaderMenuButton: true,
    resizable: false,
    cellRenderer: renderResponseActionTriggerCell,
    cellStyle: { paddingRight: '0px', paddingLeft: '0px' }
  },
  {
    field: 'action_type',
    headerName: 'Action Info',
    minWidth: 160,
    flex: 1,
    suppressHeaderMenuButton: true,
    resizable: false,
    autoHeight: true,
    cellRenderer: renderInfoCell,
    cellStyle: {
      overflow: 'hidden',
      display: 'block'
    }
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    width: 175,
    minWidth: 175,
    sort: 'asc',
    sortIndex: 0,
    resizable: false,
    suppressHeaderMenuButton: true,
    cellRenderer: dateTimeRenderer
  }
];
